// The one index-to-index step every WAI-ARIA listbox/combobox in this app takes on
// ↑ ↓ Home End (epic #5620 slice 02). `ModelPickerList.tsx` had its own copy (the
// `jump` map, pre-extraction); `Select.tsx` is the second consumer, and
// `scripts/one-impl.test.ts` is what keeps a third from forking its own.
//
// Pure index arithmetic only — no DOM, no Solid. The caller owns clamping its own
// cursor into range before calling this (both call sites already do, via their own
// `active()`/`selected()`), and owns refusing to call it at all when `total` is 0:
// returning -1 here is a defined answer for an EMPTY list, not a license to skip
// that guard — `ModelPickerList`'s `total > 0` check stays, because arrow keys on an
// empty list must leave the cursor alone rather than snap it to -1.

export type ListboxKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

export const LISTBOX_KEYS: readonly ListboxKey[] = ['ArrowDown', 'ArrowUp', 'Home', 'End'];

/** Narrows a raw `KeyboardEvent.key` to one of the four navigation keys. */
export function isListboxKey(key: string): key is ListboxKey {
  return (LISTBOX_KEYS as readonly string[]).includes(key);
}

/**
 * The next cursor index for one of the four navigation keys, wrapping ↑/↓ at
 * either end. `total <= 0` (nothing to land on) answers -1 rather than throwing —
 * the empty-list case the caller is expected to guard around, not a rule this
 * function enforces for them.
 */
export function nextListboxIndex(current: number, total: number, key: ListboxKey): number {
  if (total <= 0) return -1;
  switch (key) {
    case 'ArrowDown':
      return (current + 1) % total;
    case 'ArrowUp':
      return (current - 1 + total) % total;
    case 'Home':
      return 0;
    case 'End':
      return total - 1;
  }
}
